import { cn } from '~/src/lib/utils'
import { useEffect, useState } from 'react'

interface Props extends React.HTMLAttributes<HTMLSpanElement> {
  speed?: number
  startDelay?: number
  enabled?: boolean
  onComplete?: () => void
  skipAnimation?: boolean
}

const Typewriter: React.FC<Props> = ({
  children,
  className,
  speed = 50,
  startDelay = 0,
  enabled = true,
  onComplete,
  skipAnimation = false,
}) => {
  const [words, setWords] = useState<string[]>([])
  const [currentIndex, setCurrentIndex] = useState(startDelay === 0 ? 0 : -1)
  const [isDelayComplete, setIsDelayComplete] = useState(startDelay === 0)

  // If skipAnimation is true, set the current index to the last word
  useEffect(() => {
    if (skipAnimation) {
      setCurrentIndex(words.length - 1)
    }
  }, [skipAnimation, words])

  useEffect(() => {
    if (!enabled) return
    const wordsArray = children?.toString().split(' ') || []
    setWords(wordsArray)

    const initialIndex = startDelay === 0 ? 0 : -1
    setCurrentIndex(initialIndex)
    setIsDelayComplete(startDelay === 0)

    if (startDelay > 0) {
      const delay = setTimeout(() => {
        setIsDelayComplete(true)
        setCurrentIndex(0)
      }, startDelay)
      return () => clearTimeout(delay)
    }
  }, [children, startDelay, enabled])

  useEffect(() => {
    if (!enabled || !isDelayComplete) return
    if (currentIndex >= 0 && currentIndex < words.length) {
      const timeout = setTimeout(() => {
        setCurrentIndex((prev) => prev + 1)
      }, speed)
      return () => clearTimeout(timeout)
    }
  }, [words, currentIndex, speed, enabled, isDelayComplete])

  useEffect(() => {
    if (currentIndex >= words.length && words.length > 0) {
      onComplete?.()
    }
  }, [currentIndex, words.length, onComplete])

  return (
    <span className={cn('inline-flex flex-wrap gap-1', className)}>
      {words.map((word, index) => (
        <span
          key={index}
          className={cn(
            `transition-opacity duration-500 ease-in-out ${
              index <= currentIndex ? 'opacity-100' : 'opacity-0'
            }`
          )}
        >
          {word}
        </span>
      ))}
    </span>
  )
}

export default Typewriter
